import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpService } from './http.service';
import { NotificationDto } from '../dtos/notification.dto';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { SpringPage, TableQuery } from 'src/app/shared/models/table-column.model';
import { takeUntil } from 'rxjs/operators';
import { WebSocketService } from './websocket.service';
import { notificationRoute } from '../utils/notification-display.util';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private httpService = inject(HttpService);
  private router = inject(Router);
  private webSocketService = inject(WebSocketService);

  private notification?: NotificationDto;
  private wsSubscription?: Subscription;

  public countUnreadChanged: BehaviorSubject<number> = new BehaviorSubject(0);
  /** Each notification pushed by the server, as it arrives - so an open list can refresh. */
  readonly received$ = new Subject<NotificationDto>();
  /**
   * A notification the reader just read - its id, or `'all'` after "mark all as read" - from
   * wherever it happened (the list, the toast), so every view shows the same state.
   */
  readonly readChanged$ = new Subject<number | 'all'>();
  private destroy$ = new Subject<void>();
  showToast: boolean = false;

  /** How long the live toast stays up, unless the pointer is resting on it. */
  readonly toastDurationMs = 6000;
  private toastTimer?: ReturnType<typeof setTimeout>;
  private toastRemainingMs = 0;
  private toastStartedAt = 0;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnDestroy() {
    this.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get getNotification(): NotificationDto | undefined {
    return this.notification;
  }

  public set setCountUnreadChanged(value: number) {
    this.countUnreadChanged.next(value);
  }

  // Method to increment the value
  public incrementCountUnreadChanged(): void {
    const currentValue: number = this.countUnreadChanged.getValue();
    const newValue: number = currentValue + 1;
    this.countUnreadChanged.next(newValue);
  }

  /** One fewer unread - never below zero, whatever order the events arrive in. */
  public decrementCountUnreadChanged(): void {
    this.countUnreadChanged.next(Math.max(0, this.countUnreadChanged.getValue() - 1));
  }

  /**
   * Connect to WebSocket and subscribe to notifications
   */
  connect() {
    // Disconnect any existing connection
    this.disconnect();

    // Connect to WebSocket
    this.wsSubscription = this.webSocketService
      .connect()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (notification: NotificationDto) => {
          this.incrementCountUnreadChanged();
          this.notification = { ...notification, opened: false };
          this.received$.next(this.notification);
          this.showLiveToast();
        },
        error: (error) => {
          console.error('WebSocket subscription error:', error);
        },
      });
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
    }
    this.webSocketService.disconnect();
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.webSocketService.isConnected();
  }

  /**
   * One page of the reader's own notifications, each with whether they read it, as the shared
   * table asks for it: RSQL over `isOpened`, `notification.type`, `notification.title`,
   * `notification.message` and `notification.sendDate`, plus page and sort.
   *
   * The list used to come from `/search`, which returns the shared notification rows: they carry
   * no read state, so everything showed as unread whatever was marked.
   */
  search(query: TableQuery): Promise<SpringPage<NotificationDto>> {
    const params: Record<string, string | number> = {
      page: query.page,
      size: query.size,
      sortBy: query.sortBy || 'sendDate',
      sortDir: query.sortDir || 'desc',
    };
    if (query.q) params['q'] = query.q;
    return this.httpService.get<SpringPage<NotificationDto>>('/v1.0/notifications/mine', params);
  }

  countUnreadByUserId(userId: number) {
    let params: any = { userId: userId };
    this.httpService
      .get('/v1.0/notifications/countUnread', params)
      .then((data: any) => {
        this.setCountUnreadChanged = data;
      })
      .catch((error) => {
        console.log(error);
      });
  }

  /**
   * Marks one notification read, for the reader only. The badge goes down only when the server
   * says it was unread until now, so opening the same one twice cannot push it out of step.
   */
  async markAsRead(notificationId: number): Promise<void> {
    const wasUnread = await this.httpService.get<boolean>('/v1.0/notifications/openNotification', { notificationId });
    if (wasUnread !== false) this.decrementCountUnreadChanged();
    this.readChanged$.next(notificationId);
  }

  /** Shows the toast for the notification just received, restarting its countdown. */
  private showLiveToast() {
    this.showToast = true;
    this.startToastTimer(this.toastDurationMs);
  }

  private startToastTimer(ms: number) {
    clearTimeout(this.toastTimer);
    this.toastRemainingMs = ms;
    this.toastStartedAt = Date.now();
    this.toastTimer = setTimeout(() => this.closeNotification(), ms);
  }

  /** The reader is on it: hold the toast until the pointer leaves. */
  pauseToast() {
    if (!this.showToast) return;
    clearTimeout(this.toastTimer);
    this.toastRemainingMs = Math.max(0, this.toastRemainingMs - (Date.now() - this.toastStartedAt));
  }

  resumeToast() {
    if (!this.showToast) return;
    this.startToastTimer(Math.max(1500, this.toastRemainingMs));
  }

  /** Marks a notification read and goes to what it is about: a request, an expense, the wallet. */
  open(notification: NotificationDto | undefined) {
    if (!notification) return;
    if (!notification.opened && notification.id) {
      notification.opened = true;
      this.markAsRead(notification.id).catch(() => (notification.opened = false));
    }
    const route = notificationRoute(notification);
    if (route) this.router.navigate(route);
  }

  openNotification() {
    this.open(this.notification);
    this.closeNotification();
  }

  closeNotification(): void {
    clearTimeout(this.toastTimer);
    this.showToast = false;
  }

  /** Marks everything the reader has not read; how many it marked. */
  async markAllAsRead(): Promise<number> {
    const marked = await this.httpService.get<number>('/v1.0/notifications/markAllAsRead');
    this.setCountUnreadChanged = 0;
    this.readChanged$.next('all');
    return Number(marked ?? 0);
  }
}
